import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Input,
    Button,
    IconButton,
    Select,
    Box,
    Divider,
} from '@chakra-ui/react';
import { Plus, Trash2 } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter, ManageDialogPrimaryButton } from './ManageDialogFrame';
import { useBIMStore } from '../../state/useBIMStore';

export function DesignOptionsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'designOptions');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const project = useBIMStore((state) => state.project);
    const selection = useBIMStore((state) => state.selection);
    const addDesignOptionSet = useBIMStore((state) => state.addDesignOptionSet);
    const addDesignOption = useBIMStore((state) => state.addDesignOption);
    const removeDesignOption = useBIMStore((state) => state.removeDesignOption);
    const setActiveDesignOption = useBIMStore((state) => state.setActiveDesignOption);
    const assignDesignOptionToElements = useBIMStore((state) =>state.assignDesignOptionToElements); const [newSetName, setNewSetName] = useState(''); const [newOptionName, setNewOptionName] = useState(''); const [selectedSetId, setSelectedSetId] = useState<string>('');

    const activeSet = project.designOptionSets.find((s) => s.id === selectedSetId) ?? project.designOptionSets[0];

    const handleAddSet = () => {
        const name = newSetName.trim();
        if (!name) return;
        const created = bimAddSet(name);
        setNewSetName('');
        if (created) setSelectedSetId(created);
    };

    // Local helper: addDesignOptionSet returns void through the store, so
    // compute the expected new set id by reading the store after refresh.
    function bimAddSet(name: string): string | null {
        const store = useBIMStore.getState();
        store.addDesignOptionSet(name);
        const sets = useBIMStore.getState().project.designOptionSets;
        const created = sets.find((s) => s.name === name);
        return created ? created.id : null;
    }

    const handleAddOption = () => {
        const name = newOptionName.trim();
        if (!name || !activeSet) return;
        addDesignOption(activeSet.id, name);
        setNewOptionName('');
    };

    const handleAssign = () => {
        if (!activeSet?.activeOptionId || selection.length === 0) return;
        assignDesignOptionToElements(selection, activeSet.activeOptionId);
    };

    const handleAssignToMainModel = () => {
        if (selection.length === 0) return;
        assignDesignOptionToElements(selection, null);
    };

    return (
        <ManageDialogFrame
            title={t('manage.designOptions')}
            isOpen={isOpen}
            onClose={onClose}
            footer={
                <ManageDialogCloseFooter
                    onClose={onClose}
                    extra={
                        <>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleAssignToMainModel}
                                isDisabled={selection.length === 0}
                                color="var(--color-text-secondary)"
                            >{t('manage.moveToMainModel')}</Button>
                            <ManageDialogPrimaryButton
                                label={selection.length > 0 ? `Assign ${selection.length} Element(s)` : 'Assign Selection'}
                                onClick={handleAssign}
                                isDisabled={selection.length === 0 || !activeSet?.activeOptionId}
                            />
                        </>
                    }
                />
            }
        >
            <VStack spacing={4} align="stretch">
                {project.designOptionSets.length === 0 ? (
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('manage.noDesignOptionSetsYetCreateOneToExploreDesignAlternatives')}</Text>
                ) : (
                    <>
                        <HStack justify="space-between">
                            <Text fontSize="sm" color="var(--color-text-secondary)" whiteSpace="nowrap">{t('manage.optionSet')}</Text>
                            <Select
                                size="sm"
                                width="220px"
                                value={activeSet?.id ?? ''}
                                onChange={(e) => setSelectedSetId(e.target.value)}
                                bg="var(--color-bg-tertiary)"
                                borderColor="var(--color-border)"
                            >
                                {project.designOptionSets.map((s) => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </Select>
                        </HStack>

                        {activeSet ? (
                            <VStack align="stretch" spacing={1}>
                                {activeSet.options.map((option) => {
                                    const isActive = activeSet.activeOptionId === option.id;
                                    const optionCount = Array.from(project.elements.values()).filter(
                                        (el) =>el.designOptionId === option.id ).length; return (<Box
                                            key={option.id}
                                            p={2}
                                            borderRadius="var(--cad-radius-sm)"
                                            borderWidth="1px"
                                            borderColor={isActive ? 'var(--color-accent)' : 'var(--color-border)'}
                                        >
                                            <HStack justify="space-between">
                                                <VStack align="start" spacing={0} minWidth={0}>
                                                    <Text fontSize="sm" isTruncated>
                                                        {option.name}
                                                        {option.isPrimary ? (
                                                            <Text as="span" fontSize="xs" color="var(--color-text-secondary)">
                                                                {' '}(Primary)
                                                            </Text>
                                                        ) : null}
                                                    </Text>
                                                    <Text fontSize="xs" color="var(--color-text-secondary)">
                                                        {optionCount} element{optionCount === 1 ? '' : 's'}
                                                    </Text>
                                                </VStack>
                                                <HStack spacing={1} flexShrink={0}>
                                                    <Button
                                                        size="xs"
                                                        variant={isActive ? 'solid' : 'ghost'}
                                                        onClick={() => setActiveDesignOption(activeSet.id, option.id)}
                                                        bg={isActive ? 'var(--color-accent)' : undefined}
                                                        color={isActive ? 'white' : undefined}
                                                    >
                                                        {isActive ? 'Active' : 'Set Active'}
                                                    </Button>
                                                    <IconButton
                                                        aria-label={`Delete ${option.name}`}
                                                        icon={<Trash2 size={12} />}
                                                        size="xs"
                                                        variant="ghost"
                                                        isDisabled={activeSet.options.length <= 1}
                                                        onClick={() => removeDesignOption(activeSet.id, option.id)}
                                                    />
                                                </HStack>
                                            </HStack>
                                        </Box>
                                    );
                                })}
                            </VStack>
                        ) : null}

                        <HStack spacing={2}>
                            <Input
                                size="sm"
                                placeholder={`New option in "${activeSet?.name ?? ''}"`}
                                value={newOptionName}
                                onChange={(e) => setNewOptionName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAddOption();
                                }}
                                bg="var(--color-bg-tertiary)"
                                borderColor="var(--color-border)"
                            />
                            <Button
                                size="sm"
                                leftIcon={<Plus size={14} />}
                                variant="ghost"
                                onClick={handleAddOption}
                                isDisabled={!newOptionName.trim() || !activeSet}
                            >{t('manage.addOption')}</Button>
                        </HStack>
                    </>
                )}

                <Divider borderColor="var(--color-border)" />

                <HStack spacing={2}>
                    <Input
                        size="sm"
                        placeholder={t('manage.newDesignOptionSetName')}
                        value={newSetName}
                        onChange={(e) => setNewSetName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddSet();
                        }}
                        bg="var(--color-bg-tertiary)"
                        borderColor="var(--color-border)"
                    />
                    <Button
                        size="sm"
                        leftIcon={<Plus size={14} />}
                        variant="ghost"
                        onClick={handleAddSet}
                        isDisabled={!newSetName.trim()}
                    >{t('manage.addSet')}</Button>
                </HStack>
            </VStack>
        </ManageDialogFrame>
    );
}
